import {Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input, ViewChild} from '@angular/core';
import {forkJoin} from 'rxjs';

import {UsersService} from '../../../services/users.service';
import {TrainingActivityViewModel} from '../../../viewModels/trainingActivity.viewModel';
import {TrainingDurationViewModel} from '../../../viewModels/trainingDuration.viewModel';
import {NotificationsService} from '../../../services/notification.service';
import {LocalisationService} from '../../../services/localisation.service';
import {MinutesToReadableTimePipe} from '../../../pipes/minutesToReadableTime.pipe';
import {AppLoadingEventsEmitter} from '../../../events/loading.events';
import {TrainingViewModel} from '../../../viewModels/training.viewModel';
import {ActivityViewModel} from '../../../viewModels/activity.viewModel';

@Component({
    selector: 'app-training-info-section',
    templateUrl: './training-info-section.component.html',
    styleUrls: ['./training-info-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class TrainingInfoSectionComponent implements OnInit {
    @Input() editable = true;
    @Input() notifications = true;

    currentlySelectedActivity: ActivityViewModel;

    trainings: TrainingViewModel[] = [];
    activities: ActivityViewModel[];

    trainingActivities: TrainingActivityViewModel[];
    trainingDurations: TrainingDurationViewModel[];

    constructor(
        private readonly usersService: UsersService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly notificationService: NotificationsService,
        private readonly localisationService: LocalisationService,
        private readonly minutesToReadableTimePipe: MinutesToReadableTimePipe,
        private readonly appLoadingEventsEmitter: AppLoadingEventsEmitter,
    ) {
    }

    ngOnInit() {
        this.appLoadingEventsEmitter.broadcastAppIsLoading(true);

        forkJoin(
            this.usersService.getTrainings(),
            this.usersService.getUserTrainingActivities(),
            this.usersService.getUserTrainingDurations(),
            this.usersService.getUserActivities(),
        )
            .subscribe(([trainings, trainingActivities, trainingDurations, activities]) => {
                this.trainings = trainings;
                this.trainingActivities = trainingActivities;
                this.trainingDurations = trainingDurations;
                this.activities = activities;
                this.currentlySelectedActivity = this.usersService.userProfile.activity ? this.activities.find(a => a.id === this.usersService.userProfile.activity.id) : undefined;

                if (this.trainings.length <= 0) {
                    this.trainings.push(new TrainingViewModel());
                }

                this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
                this.changeDetectorRef.markForCheck();
            });
    }

    updateProfile() {
        let trainingsValidationPassed = true;
        this.trainings.map(training => {
            if (
                !training.day
                || training.day.length <= 0
                || !training.training_activity
                || !training.training_duration
            ) {
                trainingsValidationPassed = false;
            }
        });

        if (!trainingsValidationPassed) {
            this.notificationService.error(this.localisationService.localise('empty_trainings_error'));
            return;
        }

        this.appLoadingEventsEmitter.broadcastAppIsLoading(true);

        forkJoin([
            this.usersService.updateUserProfile({activity: {id: this.currentlySelectedActivity.id}}),
            this.usersService.updateTrainings({trainings: this.convertToTrainingsDTO()})
        ])
            .subscribe(() => {
                this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
                this.changeDetectorRef.markForCheck();

                if (this.notifications) {
                    this.notificationService.success(this.localisationService.localise('settings_profile_updated_successfully'));
                }
            }, () => {
                this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
            });
    }

    addTraining() {
        this.trainings.push(new TrainingViewModel());
        this.changeDetectorRef.markForCheck();
    }

    removeTraining(index: number) {
        this.trainings.splice(index, 1);
        this.changeDetectorRef.markForCheck();
    }

    duplicateTraining(index: number) {
        this.trainings.push({
            day: this.trainings[index].day,
            training_activity: this.trainings[index].training_activity,
            training_duration: this.trainings[index].training_duration,
        });

        this.changeDetectorRef.markForCheck();
    }

    onDayChange(event, index: number) {
        this.trainings[index].day = event.target.value;
        this.changeDetectorRef.markForCheck();
    }

    onTrainingChange(event, index: number) {
        // tslint:disable-next-line:radix
        this.trainings[index].training_activity = this.trainingActivities.find(ta => ta.id === parseInt(event.target.value));
        this.changeDetectorRef.markForCheck();
    }

    onDurationChange(event, index: number) {
        // tslint:disable-next-line:radix
        this.trainings[index].training_duration = this.trainingDurations.find(td => td.id === parseInt(event.target.value));
        this.changeDetectorRef.markForCheck();
    }

    onActivitySelected(activity: ActivityViewModel) {
        this.currentlySelectedActivity = activity;
    }

    convertToTrainingsDTO() {
        const dtoData = [];

        this.trainings.map(training => {
            dtoData.push({
                'day': training.day,
                'training_activity_id': training.training_activity.id,
                'training_duration_id': training.training_duration.id,
            });
        });

        return dtoData;
    }


}
